// SeeTex MathField Editor Component
// Wraps MathLive's <math-field> with our custom trigger engine
// Targets MathLive 0.108.x — setOptions and onKeystroke were removed in this
// version; all config is done via element properties and DOM events.

import { useRef, useEffect, useCallback, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import "mathlive";
import {
    TriggerEngine,
    allMathTriggers,
    searchTriggers,
} from "../triggers";
import type { TriggerMatch, TriggerSuggestion } from "../triggers";
import { DocHints, type HintItem } from "./DocHints";
import { useAutocomplete } from "../contexts/AutocompleteContext";
import katex from "katex";

interface MathEditorProps {
    /** Called whenever the LaTeX content changes */
    onLatexChange?: (latex: string) => void;
    /** Called when user presses Escape */
    onEscape?: () => void;
    /** Called when the math field loses focus */
    onBlur?: () => void;
    /** Initial LaTeX value */
    initialLatex?: string;
    /** Whether the editor is focused */
    autoFocus?: boolean;
}

// Render a simple LaTeX preview to HTML
function renderPreview(latex: string): string {
    const preview = latex
        .replace(/#0/g, "a")
        .replace(/#1/g, "b")
        .replace(/#2/g, "c")
        .replace(/#\d+/g, "x")
        .replace(/#\?/g, "\\square");

    let finalPreview = preview;
    if (finalPreview === "_{a}") finalPreview = "x_{a}";
    if (finalPreview === "^{a}") finalPreview = "x^{a}";
    if (finalPreview === "_{a}^{b}") finalPreview = "x_{a}^{b}";

    try {
        return katex.renderToString(finalPreview, {
            throwOnError: false,
            displayMode: false,
            strict: "ignore",
            trust: true,
        });
    } catch {
        return "";
    }
}

function toHintItems(suggestions: TriggerSuggestion[]): HintItem[] {
    return suggestions.map(s => ({
        trigger: String(s.trigger.trigger),
        name: s.trigger.name,
        matchedOn: s.matchedOn,
        previewHtml: renderPreview(s.trigger.latex),
    }));
}

export function MathEditor({
    onLatexChange,
    onEscape,
    onBlur,
    initialLatex = "",
    autoFocus = true,
}: MathEditorProps) {
    const mathfieldRef = useRef<any>(null);
    const engineRef = useRef<TriggerEngine>(new TriggerEngine());
    const { autocompleteEnabled, hintsEnabled } = useAutocomplete();
    const [activeChoices, setActiveChoices] = useState<{
        triggerId: string;
        choices: string[];
        currentIndex: number;
    } | null>(null);

    // Pending math-font wrapper — set when mc/mr/mk/etc fires, cleared after
    // the next printable character which completes e.g. \mathcal{C}.
    const pendingWrapperRef = useRef<string | null>(null);
    const [pendingWrapper, setPendingWrapper] = useState<string | null>(null);

    // Tracks the most recently fired trigger so that pressing Escape immediately
    // after a trigger fires undoes the expansion and re-inserts only the
    // single character that was blocked by preventDefault (e.g. 't' from 'dist').
    const lastTriggerRef = useRef<{ blockedChar: string; preLatex: string } | null>(null);

    // "mm" detection inside \text{} → switch to math mode.
    // Set to true when 'm' is typed in text mode; if the next key is also 'm'
    // in text mode, we intercept it and switch to math.
    const textModePendingMRef = useRef(false);

    // Hint system state for beginner discoverability
    const [hintWord, setHintWord] = useState<string>(""); // Current word being typed
    const [hintIndex, setHintIndex] = useState<number>(0); // Selected hint index
    const [hintsVisible, setHintsVisible] = useState<boolean>(false);
    const [hintPos, setHintPos] = useState<{ top: number; left: number } | null>(null);

    // Get suggestions based on current word (only when hints are enabled)
    const hintSuggestions = useMemo(() => {
        if (!hintsEnabled) return [];
        if (hintWord.length < 3) return [];
        return searchTriggers(hintWord, 6);
    }, [hintWord, hintsEnabled]);

    // Register triggers once, independent from MathLive mount lifecycle.
    // This avoids a failure mode where setup() exits early and triggers never load.
    // ALL triggers (structural, simple, regex) go through our engine — MathLive's
    // inline shortcuts are disabled to prevent pending-shortcut state from
    // corrupting insertions (e.g. typing 'm' starts a MathLive shortcut timer
    // for 'mp→\mp', then our engine fires 'mc→\mathcal' and the stale timer
    // interferes with the inserted structure).
    useEffect(() => {
        engineRef.current.registerTriggers(allMathTriggers);
    }, []);

    // Convert #0, #1, ... placeholders to MathLive's #? placeholder syntax
    const convertPlaceholders = useCallback((latex: string): string => {
        return latex.replace(/#(\d+)/g, "#?");
    }, []);

    // Clear hint state
    const clearHints = useCallback(() => {
        setHintWord("");
        setHintIndex(0);
        setHintsVisible(false);
    }, []);

    const clearPending = useCallback(() => {
        pendingWrapperRef.current = null;
        setPendingWrapper(null);
    }, []);

    // Show/hide hints based on suggestions
    useEffect(() => {
        if (hintSuggestions.length > 0 && hintWord.length >= 3) {
            setHintsVisible(true);
            setHintIndex(0); // Reset selection when suggestions change
        } else {
            setHintsVisible(false);
        }
    }, [hintSuggestions, hintWord]);

    // Execute a trigger match in MathLive.
    // lastCharBlocked: when true (beforeinput path) the triggering character was
    // blocked by ev.preventDefault() so it was never inserted — only delete
    // (consumed - 1) preceding characters instead of consumed.
    const executeTrigger = useCallback(
        (match: TriggerMatch, lastCharBlocked: boolean = false) => {
            const mf = mathfieldRef.current;
            if (!mf) return;

            // Save latex snapshot before any modifications so Escape can restore
            // to exactly this state without touching MathLive's undo stack.
            const preLatex = mf.getValue();
            lastTriggerRef.current = { blockedChar: "", preLatex };

            const def = match.trigger;
            let latex: string;

            if (def.isRegex && def.dynamicGenerator) {
                latex = def.dynamicGenerator(match.captures);
            } else {
                latex = def.latex;
            }

            // Delete the trigger characters already in the field.
            // The triggering char was blocked by preventDefault so skip it.
            const deleteCount = lastCharBlocked
                ? Math.max(0, match.consumed - 1)
                : match.consumed;
            if (import.meta.env.DEV) console.log(`[executeTrigger] trigger="${String(def.trigger)}" category="${def.category}" consumed=${match.consumed} deleteCount=${deleteCount} lastCharBlocked=${lastCharBlocked}`);
            for (let i = 0; i < deleteCount; i++) {
                mf.executeCommand("deleteBackward");
            }

            // Math-font triggers (mc→\mathcal, mk→\mathbb, tt→\text, etc.) —
            if (def.category === "math-font") {
                // e.g. "\\mathcal{#0}" → "\\mathcal"
                const baseCmd = latex.replace(/\{#\d+\}$/, "");
                pendingWrapperRef.current = baseCmd;
                setPendingWrapper(baseCmd);
                return;
            }

            const mlLatex = convertPlaceholders(latex);
            mf.insert(mlLatex, {
                focus: true,
                feedback: false,
                selectionMode: mlLatex.includes("#?") ? "placeholder" : "after",
            });

            if (def.choices && def.choices.length > 1) {
                engineRef.current.setActiveChoice(def.id, def.choices);
                setActiveChoices({
                    triggerId: def.id,
                    choices: def.choices,
                    currentIndex: 0,
                });
            }
        },
        [convertPlaceholders]
    );

    // Apply selected hint - execute the trigger via executeTrigger for proper
    // handling of math-font triggers (mc, mr, etc.) and other special categories.
    const applyHint = useCallback((suggestion: TriggerSuggestion) => {
        const mf = mathfieldRef.current;
        if (!mf) return;

        // Delete the word we typed (hintWord characters)
        for (let i = 0; i < hintWord.length; i++) {
            mf.executeCommand("deleteBackward");
        }

        // Build a TriggerMatch and route through executeTrigger
        const match: TriggerMatch = {
            trigger: suggestion.trigger,
            consumed: 0, // already deleted above
            captures: [],
        };
        executeTrigger(match, false);

        clearHints();
    }, [hintWord, executeTrigger, clearHints]);

    // ── One-time setup: configure element properties + register triggers ────────
    // MathLive throws "Mathfield not mounted" if you call setValue/focus before
    // its internal engine is ready. Wait for the 'mount' event, which MathLive
    // fires once the math-field custom element is fully initialised.
    useEffect(() => {
        const mf = mathfieldRef.current;
        if (!mf) return;

        const setup = () => {
            // MathLive 0.108 exposes options as element properties (setOptions removed)
            mf.smartMode = false;
            mf.smartFence = true;
            mf.smartSuperscript = false;
            // Disable MathLive's native inline shortcuts entirely — our TriggerEngine
            // handles all triggers to avoid pending-shortcut state conflicts.
            mf.inlineShortcuts = {};
            mf.inlineShortcutTimeout = 0;
            // Silence sounds
            (mf as any).keypressSound = null;
            (mf as any).plonkSound = null;
            // Remove the hamburger menu and virtual keyboard buttons
            (mf as any).menuItems = [];
            (mf as any).virtualKeyboardMode = "off";

            if (initialLatex) {
                mf.setValue(initialLatex);
            }

            if (autoFocus) {
                // MathLive's internal _mathfield can be in a bad state after
                // React StrictMode's unmount/remount cycle. focus() may throw
                // "this.mathfield.options is undefined". Retry until it works.
                const tryFocus = (attempts: number = 0) => {
                    if (attempts > 10) return; // give up after ~10 frames
                    try {
                        if ((mf as any)._mathfield && document.contains(mf)) {
                            mf.focus();
                        } else {
                            requestAnimationFrame(() => tryFocus(attempts + 1));
                        }
                    } catch {
                        requestAnimationFrame(() => tryFocus(attempts + 1));
                    }
                };
                requestAnimationFrame(() => tryFocus(0));
            }
        };

        // If MathLive is already ready (e.g. re-render), run setup immediately;
        // otherwise wait for the 'mount' event it emits once connected.
        if ((mf as any)._mathfield) {
            setup();
        } else {
            mf.addEventListener("mount", setup, { once: true } as any);
        }

        return () => {
            mf.removeEventListener("mount", setup);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Keyboard interception ───────────────────────────────────────────────────
    useEffect(() => {
        const mf = mathfieldRef.current;
        if (!mf) return;

        // keydown in capture phase — fires before MathLive's own keydown handler.
        // MathLive explicitly checks evt.defaultPrevented at the top of its keydown
        // handler (line 31581 of mathlive.mjs) and bails out, so preventDefault()
        // here is the ONLY reliable way to block character insertion.
        const handleKeydown = (ev: KeyboardEvent) => {
            if (!(mf as any).hasFocus?.()) return;

            const isTextMode = mf.mode === 'text';

            // ── "mm" in text mode → exit \text{} into math mode ─────────────
            // When inside a \text{} atom (e.g. cases condition) and the user
            // types "mm", delete the first 'm' and switch to math mode so
            // subsequent typing produces math notation.
            if (isTextMode && ev.key === 'm' && textModePendingMRef.current) {
                ev.preventDefault();
                textModePendingMRef.current = false;
                mf.executeCommand('deleteBackward');
                engineRef.current.clearBuffer();
                clearHints();
                mf.executeCommand(['switch-mode', 'math']);
                return;
            }
            textModePendingMRef.current = false;

            const hasPending = !!pendingWrapperRef.current;
            // After mc/mr/mk/etc fires, pendingWrapperRef holds e.g. "\\mathcal".
            // The next printable char completes it: insert \mathcal{C} in one shot.
            if (pendingWrapperRef.current) {
                if (ev.key === "Escape") {
                    clearPending();
                    return;
                }
                if (ev.key.length === 1 && !ev.ctrlKey && !ev.metaKey && !ev.altKey) {
                    ev.preventDefault();
                    const cmd = pendingWrapperRef.current;
                    clearPending();
                    clearHints();
                    engineRef.current.clearBuffer();
                    const toInsert = `${cmd}{${ev.key}}`;
                    mf.insert(toInsert, {
                        focus: true,
                        feedback: false,
                        selectionMode: "after",
                    });
                    // For \text{}, move cursor back inside the text atom so
                    // subsequent typing continues in text mode (not math mode).
                    if (cmd === "\\text") {
                        mf.executeCommand("moveToPreviousChar");
                    }
                    return;
                }
                // Non-printable key — ignore modifier-only keys (Shift, Ctrl,
                // Alt, Meta) since they precede the actual printable character.
                // Any other non-printable key (arrow, etc.) cancels the pending wrapper.
                if (ev.key.length > 1 && !["Shift", "Control", "Alt", "Meta"].includes(ev.key)) {
                    clearPending();
                }
            }

            // ── Hint system: handle navigation and selection when hints visible ──
            if (hintsVisible && hintSuggestions.length > 0) {
                if (ev.key === "ArrowDown") {
                    ev.preventDefault();
                    ev.stopPropagation();
                    setHintIndex((i) => Math.min(i + 1, hintSuggestions.length - 1));
                    return;
                }
                if (ev.key === "ArrowUp") {
                    ev.preventDefault();
                    ev.stopPropagation();
                    setHintIndex((i) => Math.max(i - 1, 0));
                    return;
                }
                if (ev.key === "Enter" || (ev.key === "Tab" && !ev.shiftKey)) {
                    ev.preventDefault();
                    ev.stopPropagation();
                    applyHint(hintSuggestions[hintIndex]);
                    return;
                }
                if (ev.key === "Escape") {
                    ev.preventDefault();
                    ev.stopPropagation();
                    clearHints();
                    return;
                }
            }

            // Enter when auto-expand is OFF and no popup: try to expand buffer
            if (ev.key === "Enter" && !autocompleteEnabled) {
                const bufferMatch = engineRef.current.checkBuffer();
                if (bufferMatch) {
                    ev.preventDefault();
                    ev.stopPropagation();
                    executeTrigger(bufferMatch, false);
                    clearHints();
                    return;
                }
            }

            if (ev.key === "Escape") {
                // If a trigger just fired, undo the expansion and re-insert only
                // the single character that was blocked (e.g. 't' from 'dist').
                // We must NOT re-insert the full trigger string — MathLive would
                // re-expand it internally.
                if (lastTriggerRef.current) {
                    const { blockedChar, preLatex } = lastTriggerRef.current;
                    lastTriggerRef.current = null;
                    engineRef.current.clearBuffer();
                    // Restore the field to the state before the trigger fired,
                    // bypassing MathLive's undo stack (which overshoots to before
                    // the math node was created).
                    mf.setValue(preLatex);
                    requestAnimationFrame(() => {
                        mf.focus();
                        mf.executeCommand("moveToMathfieldEnd");
                        if (blockedChar) {
                            mf.insert(blockedChar, { focus: true, feedback: false, selectionMode: "after" });
                        }
                    });
                    ev.preventDefault();
                    return;
                }
                clearHints();
                onEscape?.();
                ev.preventDefault();
                return;
            }

            // Backspace on empty content → delete the math node itself
            if (ev.key === "Backspace") {
                const latex = mf.getValue("latex-expanded");
                if (!latex || latex.trim() === "") {
                    onEscape?.();
                    ev.preventDefault();
                    return;
                }
                // Otherwise let MathLive handle the backspace normally
                engineRef.current.clearBuffer();
                // Remove last character from hint word
                setHintWord((prev) => prev.slice(0, -1));
                return;
            }

            // Tab behavior:
            // When auto-expand is OFF, try to expand a trigger from the buffer first.
            // Otherwise: placeholders → move to end → exit math.
            if (ev.key === "Tab" && !ev.ctrlKey && !ev.metaKey && !ev.altKey) {
                ev.preventDefault();
                ev.stopPropagation();

                if (!autocompleteEnabled) {
                    const bufferMatch = engineRef.current.checkBuffer();
                    if (bufferMatch) {
                        executeTrigger(bufferMatch, false);
                        clearHints();
                        return;
                    }
                }

                const currentLatex = mf.getValue("latex-expanded");
                if (currentLatex.includes("\\placeholder")) {
                    mf.executeCommand("moveToNextPlaceholder");
                } else {
                    const posBefore = mf.position;
                    mf.executeCommand("moveToMathfieldEnd");
                    if (posBefore === mf.position) {
                        onEscape?.();
                    }
                }
                return;
            }

            // Allow space to insert actual space in math (MathLive handles it)
            // No special handling needed - let MathLive process the space naturally

            // Ctrl+J / Ctrl+K → cycle choice variants
            if ((ev.key === "j" || ev.key === "k") && ev.ctrlKey) {
                ev.preventDefault();
                return;
            }

            // Non-printable key (Arrow, Backspace, etc.) → clear trigger buffer, hints,
            // and the undo window for the last trigger.
            if (ev.key.length > 1 || ev.ctrlKey || ev.metaKey || ev.altKey) {
                engineRef.current.clearBuffer();
                clearHints();
                lastTriggerRef.current = null;
                return;
            }

            // Printable character: feed to trigger engine BEFORE MathLive inserts it.
            // When autocomplete is ON: if a trigger matches, fire it immediately
            // (preventDefault stops MathLive from inserting the char).
            // When autocomplete is OFF: don't auto-fire — just accumulate hint word
            // and let the user pick from the popup with Enter.
            lastTriggerRef.current = null;

            if (autocompleteEnabled) {
                const match = engineRef.current.feedChar(ev.key);
                if (import.meta.env.DEV) console.log(`[handleKeydown] feedChar("${ev.key}") → match=${match ? `"${String(match.trigger.trigger)}"` : 'none'}`);

                if (match) {
                    ev.preventDefault();
                    executeTrigger(match, true);
                    const lt = lastTriggerRef.current as { blockedChar: string; preLatex: string } | null;
                    if (lt) {
                        lt.blockedChar = ev.key;
                    }
                    clearHints();
                } else {
                    // Synthetic (replayed) keydown events are isTrusted===false;
                    // MathLive ignores them for character insertion. Insert
                    // the character programmatically so replay works.
                    if (!ev.isTrusted) {
                        ev.preventDefault();
                        mf.insert(ev.key, { focus: true, feedback: false, selectionMode: "after" });
                    }
                    if (/^[a-zA-Z]$/.test(ev.key)) {
                        setHintWord((prev) => prev + ev.key);
                    } else {
                        clearHints();
                    }
                }
            } else {
                // No auto-fire — just append to buffer (don't match/consume)
                engineRef.current.appendChar(ev.key);
                if (!ev.isTrusted) {
                    ev.preventDefault();
                    mf.insert(ev.key, { focus: true, feedback: false, selectionMode: "after" });
                }
                if (/^[a-zA-Z]$/.test(ev.key)) {
                    setHintWord((prev) => prev + ev.key);
                } else {
                    clearHints();
                }
            }

            // Track 'm' in text mode for "mm" → math-mode-switch detection.
            // Only reaches here for printable chars that weren't intercepted above.
            if (isTextMode && ev.key === 'm') {
                textModePendingMRef.current = true;
            }
        };

        document.addEventListener("keydown", handleKeydown, true);

        return () => {
            document.removeEventListener("keydown", handleKeydown, true);
        };
    }, [executeTrigger, onEscape, hintsVisible, hintSuggestions, hintIndex, applyHint, clearHints, clearPending, autocompleteEnabled]);

    // ── Notify parent on every content change ───────────────────────────────────
    useEffect(() => {
        const mf = mathfieldRef.current;
        if (!mf) return;

        const handleInput = () => onLatexChange?.(mf.getValue("latex-expanded"));
        mf.addEventListener("input", handleInput);
        return () => mf.removeEventListener("input", handleInput);
    }, [onLatexChange]);

    // ── Popup position: place below mathfield using fixed coords ──────────────
    useEffect(() => {
        if (!hintsVisible) return;
        const mf = mathfieldRef.current;
        if (!mf) return;
        const rect = mf.getBoundingClientRect();
        setHintPos({ top: rect.bottom + 4, left: rect.left });
    }, [hintsVisible, hintSuggestions]);

    // ── Blur notification ───────────────────────────────────────────────────────
    useEffect(() => {
        const mf = mathfieldRef.current;
        if (!mf) return;
        const handleBlur = () => onBlur?.();
        mf.addEventListener("blur", handleBlur);
        return () => mf.removeEventListener("blur", handleBlur);
    }, [onBlur]);

    return (
        <div className="math-editor-container">
            <math-field
                ref={mathfieldRef}
                class="seetex-mathfield"
                virtual-keyboard-mode="off"
                smart-mode="off"
                keypress-sound="none"
                plonk-sound="none"
            />
            {pendingWrapper && (
                <div className="pending-wrapper-indicator">
                    <span className="pending-wrapper-label">
                        {pendingWrapper.replace(/^\\/, "")}&thinsp;&hellip;
                    </span>
                </div>
            )}
            {activeChoices && (
                <div className="choice-indicator">
                    <span className="choice-label">
                        {activeChoices.choices[activeChoices.currentIndex]
                            .replace(/\\frac/, "frac")
                            .replace(/\\dfrac/, "dfrac")
                            .replace(/\\tfrac/, "tfrac")
                            .replace(/\{#\??\}/g, "")
                            .replace(/\\/g, "")}
                    </span>
                    <span className="choice-hint">Ctrl+J/K to cycle</span>
                </div>
            )}
            {createPortal(
                <DocHints
                    suggestions={toHintItems(hintSuggestions)}
                    selectedIndex={hintIndex}
                    onSelect={(index) => {
                        setHintIndex(index);
                        applyHint(hintSuggestions[index]);
                    }}
                    visible={hintsVisible}
                    position={hintPos}
                />,
                document.body
            )}
        </div>
    );
}
